import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import { Button } from "../../@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";
import { Spinner } from "@/components/spinner";

const getUserId = (row) =>
  row?.user_id ?? row?.userId ?? row?.id ?? row?._id ?? null;

function normalizeRoleMeta(role) {
  return {
    id: role?.id ?? role?.roleId ?? role?.role_id,
    title: role?.title ?? role?.roleTitle ?? role?.role_title ?? role?.role,
  };
}

export default function EditUserRolesBulkDialog({
  users = [],
  open,
  onOpenChange,
  onUpdated,
}) {
  const { getToken } = useAuth();

  const [allRoles, setAllRoles] = useState([]);
  const [assignedRoles, setAssignedRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedUserIds = useMemo(() => {
    const rows = Array.isArray(users) ? users : [];
    const ids = rows
      .map((u) => getUserId(u))
      .filter((id) => id !== null && id !== undefined && String(id).trim() !== "")
      .map((id) => String(id));
    return Array.from(new Set(ids));
  }, [users]);

  const selectedCount = selectedUserIds.length;

  const resetState = () => {
    setAssignedRoles([]);
    setSelectedRoleId("");
  };

  useEffect(() => {
    if (!open) return;
    resetState();
  }, [open]);

  const fetchRoles = async () => {
    setLoadingRoles(true);
    setAllRoles([]);

    try {
      const token = await getToken();
      if (!token) return;

      const res = await axios.get(api_endpoint + "/user/roles", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const raw = Array.isArray(res.data?.roles) ? res.data.roles : [];
      const formatted = raw
        .map(normalizeRoleMeta)
        .filter((r) => r.id != null && String(r.title || "").trim().length);

      setAllRoles(formatted);
    } catch (error) {
      console.error("Failed to fetch roles", error);
      toast.error("Failed to load roles.");
      setAllRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const assignedRoleIdSet = useMemo(() => {
    return new Set((assignedRoles || []).map((r) => String(r.roleId)));
  }, [assignedRoles]);

  const addableRoles = useMemo(() => {
    return (allRoles || []).filter((r) => !assignedRoleIdSet.has(String(r.id)));
  }, [allRoles, assignedRoleIdSet]);

  const addRoleToList = (roleId) => {
    const meta = (allRoles || []).find((r) => String(r.id) === String(roleId));
    const roleTitle = meta?.title || "Role";

    setAssignedRoles((prev) => {
      const exists = (prev || []).some((r) => String(r.roleId) === String(roleId));
      if (exists) return prev;
      return [...(prev || []), { roleId, roleTitle }];
    });
  };

  const removeRoleFromList = (roleId) => {
    setAssignedRoles((prev) =>
      (prev || []).filter((r) => String(r.roleId) !== String(roleId))
    );
  };

  const handleSave = async () => {
    if (selectedCount < 2) {
      toast.error("Select at least 2 users to bulk edit roles.");
      return;
    }

    const roleIds = (assignedRoles || [])
      .map((r) => Number(r.roleId))
      .filter((n) => Number.isFinite(n));

    setSaving(true);
    try {
      const token = await getToken();
      if (!token) return;

      const payload = {
        updates: selectedUserIds.map((userId) => ({
          user_id: userId,
          role_ids: roleIds,
        })),
      };

      const res = await axios.put(
        api_endpoint + "/user/users/roles/edit/bulk",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updatedUsers = Number(res.data?.updated_users ?? 0) || selectedCount;
      toast.success(`Roles updated for ${updatedUsers} user${updatedUsers === 1 ? "" : "s"}.`);

      await Promise.resolve(
        onUpdated?.({
          updatedUsers,
          roleIds,
          selectedUserIds,
        })
      );

      onOpenChange(false);
    } catch (error) {
      console.error("Failed to bulk edit roles", error);
      toast.error("Failed to update roles. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const isBusy = loadingRoles || saving;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg h-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-(--neutral-900)">
            Edit User Roles
          </DialogTitle>
          <DialogDescription className="font-body text-(--neutral-700)">
            Replace roles for <span className="font-semibold">{selectedCount}</span> selected
            user{selectedCount === 1 ? "" : "s"}. Leaving this empty will remove all roles.
          </DialogDescription>
        </DialogHeader>

        {loadingRoles ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="font-body text-sm text-(--neutral-700)">Add role</div>
              <Select
                value={selectedRoleId}
                onValueChange={(next) => {
                  setSelectedRoleId(next);
                  if (!next) return;
                  addRoleToList(next);
                  setSelectedRoleId("");
                }}
                disabled={isBusy}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={loadingRoles ? "Loading roles..." : "Select a role"} />
                </SelectTrigger>
                <SelectContent>
                  {addableRoles.map((role) => (
                    <SelectItem key={String(role.id)} value={String(role.id)}>
                      {role.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="font-body text-sm text-(--neutral-700)">Selected roles</div>
              {assignedRoles.length === 0 ? (
                <div className="mt-2 text-sm font-body text-(--neutral-500)">
                  No roles selected.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 mt-2">
                  {assignedRoles.map((r) => (
                    <div key={String(r.roleId)} className="tag-chip">
                      <span className="font-body">{r.roleTitle}</span>
                      <button
                        type="button"
                        onClick={() => removeRoleFromList(r.roleId)}
                        className="chip-remove"
                        disabled={isBusy}
                        aria-label={`Remove ${r.roleTitle}`}
                        title={`Remove ${r.roleTitle}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button
                  variant="outline"
                  disabled={isBusy}
                  onClick={() => {
                    resetState();
                  }}
                >
                  Cancel
                </Button>
              </DialogClose>

              <Button type="button" variant="" disabled={isBusy || selectedCount < 2} onClick={handleSave}>
                {saving ? <ClipLoader size={18} color="#fff" /> : "Save"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
